/*
 *
 * CostsShopSelector
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 02-Oct-2025
*/

/*
 * 💡 COMPONENT OVERVIEW
 *
 * CostsShopSelector is used to show a list of "Parent" entities (e.g. payment groups or locations),
 * each containing a list of "Child" elements (e.g. payment contracts).
 *
 * The user can:
 * - select/deselect individual shops
 * - select/deselect all shops of a parent
 * - use "Select all" or "Clear all" for the full group
 * - filter content using a search bar
 *
 * It works together with ShopPickerPopup.
 *
 * ⚠️ The selection state is local and synchronized upward via `onChange`.
 * ⚠️ It also supports pre-populating the state via `selections` or fallback `shopSaved`.
 *
 */

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import { ChevronDown } from '@nexidigital/iconlibrary'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import PortalSearchBar from '@nexidigital/portalsearchbar'
import MerchantTableBadge from '@nexidigital/merchanttablebadge'
import PortalIconButton from '@nexidigital/portaliconbutton'
import PortalLoader from '@nexidigital/portalloader'
import { capitalizeFirstLetter, nullSafeString } from 'Utility/globalMethods'
import { mergeSelections } from 'Utility/shopListNormalizer'
import { useIntl } from 'react-intl'
import formattedMessage from './languageModule'
import './CostsShopSelector.scss'

const MAX_AUTO_EXPANDED_PARENTS = 3

const CostsShopSelector = ({ data, onChange, tabSelected, clearAction, actions, shopListLoading, shopSearchTerm, shopLocalSelection, noParentRowBorder }) => {
  const { multiPiva, title, label, code, group, initialParents, selections } = data

  const [parentsState, setParentsState] = useState([])
  const [visibleChildren, setVisibleChildren] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [searchedValue, setSearchedValue] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const initialExpansionInitialized = useRef(false)
  const searchBarContainerRef = useRef(null)

  const intl = useIntl()

  // CostsShopSelector is always in radiobox mode for parents and checkbox for children

  // Keep the initial selector state unselected. Applied selections are restored
  // only from Redux or the explicit selections prop.
  const normalizeCostsShopData = (parents) => {
    if (!parents || !Array.isArray(parents)) return []

    return parents.map(parent => ({
      ...parent,
      isSelected: false,
      shops: Array.isArray(parent.shops)
        ? parent.shops.map(child => ({
          ...child,
          isSelected: false
        }))
        : parent.shops
    }))
  }

  function debounce (func, delay) {
    let timeout
    return (...args) => {
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), delay)
    }
  }

  // Semplice debounce che si affida all'AbortController dell'API per gestire la cancellazione
  const debouncedSearch = useRef(
    debounce((value) => {
      const trimmed = value.trim()

      if (trimmed === '') {
        setSearchedValue(false)
        setIsLoading(true)
        // Reset: chiama API senza filtro per ripristinare la lista completa
        actions.doGetCostsShopList({ filter: '', currentGroup: originalGroup })
      } else if (trimmed.length >= 3) {
        setSearchedValue(true)
        setIsLoading(true)
        // L'AbortController nell'API si occupa di cancellare le richieste precedenti
        actions.doGetCostsShopList({ filter: trimmed, currentGroup: originalGroup })
      }
    }, 300)
  ).current

  // Il group ora è già nel formato originale (INTESA_SANPAOLO, NEXI_DIRECT, etc.)
  // Non serve più la conversione
  const originalGroup = group

  useEffect(() => {
    // Reset isLoading quando arrivano i dati
    setIsLoading(false)

    if (typeof shopSearchTerm === 'string') {
      setSearchTerm(shopSearchTerm)

      if (shopSearchTerm.trim().length >= 3) {
        setSearchedValue(true)
      }
    }

    // Normalizza i dati dal backend per evitare inconsistenze
    const normalizedParents = normalizeCostsShopData(initialParents)

    const sourceSelections =
      selections?.length > 0
        ? selections
        : shopLocalSelection?.[group] || []

    // Se ci sono selezioni locali, usale MA applica logica radiobox esclusiva
    if (sourceSelections.length > 0) {
      // In modalità radiobox costs: assicurati che ci sia solo un parent selezionato
      let selectedParentCode = null
      let selectedChildren = []

      // Trova il primo parent selezionato nelle selezioni
      for (const selection of sourceSelections) {
        if (selection.parent?.isSelected) {
          selectedParentCode = selection.parent.code
          selectedChildren = selection.shops || []
          break
        }
      }

      // Applica la selezione radiobox esclusiva
      const radioboxState = normalizedParents.map(parent => {
        if (parent.code === selectedParentCode) {
          // Parent selezionato: applica le selezioni dei figli
          return {
            ...parent,
            isSelected: true,
            shops: (parent.shops || []).map(child => {
              const selectedChild = selectedChildren.find(sc => sc.code === child.code)
              return {
                ...child,
                isSelected: selectedChild?.isSelected || false
              }
            })
          }
        } else {
          // Altri parent: deseleziona tutto
          return {
            ...parent,
            isSelected: false,
            shops: (parent.shops || []).map(child => ({
              ...child,
              isSelected: false
            }))
          }
        }
      })

      setParentsState(JSON.parse(JSON.stringify(radioboxState)))
    } else {
      // Nessuna selezione locale o esterna: mostra tutti gli elementi non selezionati
      setParentsState(JSON.parse(JSON.stringify(normalizedParents)))
    }

    const isSearchResult = typeof shopSearchTerm === 'string' && shopSearchTerm.trim().length >= 3

    if (!initialExpansionInitialized.current && !isSearchResult && normalizedParents.length > 0) {
      const shouldAutoExpand = normalizedParents.length <= MAX_AUTO_EXPANDED_PARENTS
      setVisibleChildren(
        shouldAutoExpand
          ? normalizedParents.reduce((visibleChildren, parent, index) => {
            if (parent.shops?.length > 0) visibleChildren[index] = true
            return visibleChildren
          }, {})
          : {}
      )
      initialExpansionInitialized.current = true
    }
  }, [initialParents, selections, group, actions])

  // Sync local search term with props when it changes (e.g., on tab switch reset)
  useEffect(() => {
    if (typeof shopSearchTerm === 'string') {
      setSearchTerm(shopSearchTerm)
      setSearchedValue(shopSearchTerm.trim().length >= 3)
    }
  }, [shopSearchTerm])

  // Ripristina il focus sulla search bar dopo l'aggiornamento dei risultati della ricerca
  useEffect(() => {
    // Ripristina il focus sulla search bar se c'è una ricerca attiva
    // Questo previene la perdita di focus durante la digitazione quando i risultati vengono aggiornati
    // Usa shopSearchTerm invece di searchTerm perché searchTerm potrebbe non essere ancora aggiornato
    if (shopSearchTerm && searchBarContainerRef.current) {
      // Usa setTimeout per assicurarsi che il DOM sia stato aggiornato
      setTimeout(() => {
        const input = searchBarContainerRef.current?.querySelector('input')

        if (input && document.activeElement !== input) {
          input.focus()
          // Ripristina la posizione del cursore alla fine del testo
          const length = input.value.length
          input.setSelectionRange(length, length)
        }
      }, 0)
    }
  }, [initialParents, shopSearchTerm])

  // Rimosso il secondo useEffect che causava problemi sovrascrivendo lo stato normalizzato

  const handleChange = (newState) => {
    const hasAnySelection = newState.some(p =>
      p.isSelected || p.shops?.some(c => c.isSelected)
    )

    // SEMPRE pulisci gli altri gruppi quando c'è una selezione in questo gruppo
    // Un punto vendita può essere selezionato solo in UN gruppo alla volta
    if (hasAnySelection) {
      clearAction?.(group)
    }

    setParentsState(newState)

    const updated = newState
      .map((p) => {
        const parentBase = {
          title: p.title,
          label: p.label,
          code: p.code,
          address: nullSafeString(() => p.address, ''),
          labelSelection: p.labelSelection,
          shopId: p.shopId
        }

        return {
          parent: {
            ...parentBase,
            isSelected: p.isSelected
          },
          shops: (p.shops || [])
            .filter(c => c.isSelected) // FILTRA SOLO I FIGLI SELEZIONATI
            .map((c) => ({
              code: c.code,
              isSelected: c.isSelected,
              shopId: c.shopId
            }))
        }
      })
      .filter((entry) => entry.parent.isSelected || entry.shops.length > 0) // FILTRA SOLO ENTRY CON PARENT SELEZIONATO O FIGLI SELEZIONATI

    actions.doSetCostsShopLocalSelection({ group, data: updated })
    onChange({ group, updated })
  }

  const handleParentToggle = (i) => {
    const isCurrentlySelected = parentsState[i].isSelected

    // In modalità radiobox: se clicchi su un parent già selezionato, non fare nulla
    if (isCurrentlySelected) {
      return
    }

    const updated = parentsState.map((parent, parentIndex) => {
      if (parentIndex === i) {
        // Parent corrente: seleziona il parent e tutti i suoi figli (se esistono)
        return {
          ...parent,
          isSelected: true,
          shops: (parent.shops || []).map(child => ({
            ...child,
            isSelected: true
          }))
        }
      } else {
        // MODALITÀ RADIOBOX: deseleziona SEMPRE tutti gli altri parent e i loro figli
        return {
          ...parent,
          isSelected: false,
          shops: (parent.shops || []).map(child => ({
            ...child,
            isSelected: false
          }))
        }
      }
    })

    handleChange(updated)
  }

  const handleChildToggle = (pi, ci) => {
    // SEMPLICE come nello ShopSelector originale!
    const updated = [...parentsState]
    const shop = {
      ...updated[pi].shops[ci],
      isSelected: !updated[pi].shops[ci].isSelected
    }

    delete shop.selected
    updated[pi].shops[ci] = shop

    // Aggiorna il parent: deve essere selezionato se ha almeno un figlio selezionato
    const hasSelectedChildren = updated[pi].shops.some(child => child.isSelected)
    updated[pi].isSelected = hasSelectedChildren

    // Se sto selezionando, deseleziona gli altri parent (esclusività)
    if (shop.isSelected) {
      for (let i = 0; i < updated.length; i++) {
        if (i !== pi) {
          updated[i].isSelected = false
          updated[i].shops.forEach(child => {
            child.isSelected = false
            delete child.selected
          })
        }
      }
    }

    setParentsState(updated)

    const parentCodeBeingToggled = updated[pi].code
    const previousSelections = shopLocalSelection?.[group] || []

    // Build the Redux entry for the toggled parent with all its currently visible shops
    const toggledParentEntry = {
      parent: {
        title: updated[pi].title,
        label: updated[pi].label,
        code: updated[pi].code,
        address: updated[pi].address || '',
        labelSelection: updated[pi].labelSelection,
        shopId: updated[pi].shopId,
        isSelected: updated[pi].isSelected
      },
      shops: updated[pi].shops.map(c => ({ code: c.code, isSelected: c.isSelected, shopId: c.shopId }))
    }

    const prevSameParent = previousSelections.filter(e => e.parent.code === parentCodeBeingToggled)
    let finalData

    if (shop.isSelected) {
      // Selecting: need to distinguish same-parent vs different-parent (radiobox switch)
      const prevSelectedParent = previousSelections.find(e => e.parent.isSelected || e.shops.some(s => s.isSelected))

      if (!prevSelectedParent || prevSelectedParent.parent.code === parentCodeBeingToggled) {
        // Same parent (or no prior selection): merge to preserve hidden siblings of this parent
        finalData = mergeSelections(prevSameParent, [toggledParentEntry])
      } else {
        // Different parent: radiobox switch → discard all old selections
        finalData = [toggledParentEntry]
      }
    } else {
      // Deselecting: merge only with same-parent previous selections to preserve hidden siblings
      finalData = mergeSelections(prevSameParent, [toggledParentEntry])
      // Recalculate parent.isSelected from merged shops and remove fully-deselected parents
      finalData = finalData
        .map(e => ({ ...e, parent: { ...e.parent, isSelected: e.shops.some(s => s.isSelected) } }))
        .filter(e => e.parent.isSelected)
    }

    actions.doSetCostsShopLocalSelection({ group, data: finalData })
    onChange({ group, updated: finalData })
  }

  const handleChangeSearch = (value) => {
    setSearchTerm(value)
    actions.doSetShopSearchTerm(value)

    // Mostra loader immediatamente quando l'utente digita 3+ caratteri o cancella tutto
    const trimmed = value.trim()
    if (trimmed.length >= 3 || trimmed === '') {
      setIsLoading(true)
    } else {
      // Se l'utente cancella a 1-2 caratteri, ferma il loader (nessuna chiamata API)
      setIsLoading(false)
    }

    // Usa sempre il debounce - l'AbortController nell'API gestisce le race conditions
    debouncedSearch(value)
  }

  const handleSearch = () => {
    const trimmed = searchTerm.trim()
    if (trimmed.length < 3 && trimmed !== '') return

    if (trimmed === '') {
      setSearchedValue(false)
    } else {
      setSearchedValue(true)
    }

    actions.doGetCostsShopList({ filter: trimmed, currentGroup: originalGroup })
  }

  const filteredParents = parentsState

  return (
    <Toggle>
      <div className={`shopSelectorCosts ${multiPiva ? 'multiPiva' : ''}`}>
        {multiPiva && (
          <div className='shopSelectorPiva'>
            <div className='shopSelectorPivaContainer'>
              <div className='shopSelectorPivaContent'>
                {title && <p className='pivaLabel'>{title}</p>}
                <div className='pivaRow'>
                  <p className='pivaCode'>
                    {label}: <div className='codeBox'>{code}</div>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='shopSelectorSearchBar' ref={searchBarContainerRef}>
          <PortalSearchBar
            placeholder={formattedMessage.searchBarLabel}
            cleanField
            value={searchTerm}
            onChange={(e) => {
              handleChangeSearch(e)
            }}
            onEnter={() => handleSearch()}
            onSearchClick={() => handleSearch()}
            liveSearch
          />
        </div>
        {searchedValue && !shopListLoading && !isLoading && (
          <div className='shopSelectorSearchResults'>
            <p>
              {formattedMessage.resultsFoundLabel}: <b>{filteredParents.length}</b>
            </p>
          </div>
        )}

        {(shopListLoading || isLoading)
          ? (
            <div className='shopPickerLoading'>
              <PortalLoader circular centered />
            </div>
            )
          : (
            <div className={`shopSelectorScroller ${filteredParents.length === 0 ? 'empty' : ''}`}>
              <div className='shopSelectorParent'>
                {(
                  filteredParents.map((parent, pi) => {
                    const hasChildren = (parent.shops || []).length > 0
                    const selected = hasChildren
                      ? parent.shops.filter((c) => c.isSelected).length
                      : parent.isSelected
                        ? 1
                        : 0
                    const childrenVisible = visibleChildren[pi]

                    return (
                      <div key={parent.code} className={`shopSelectorParentRow ${selected > 0 ? 'selection' : ''} ${!noParentRowBorder ? 'parentRowBorder' : ''}`}>
                        <div
                          className={`shopSelectorContainer ${childrenVisible ? 'open' : ''}`}
                          onClick={() => handleParentToggle(pi)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handleParentToggle(pi)
                            }
                          }}
                          role='button'
                          tabIndex={0}
                        >
                          <div className='shopSelectorCheckBox'>
                            <div
                              className='shopSelectorRadioBox'
                              onClick={(e) => {
                                e.stopPropagation()
                                handleParentToggle(pi)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleParentToggle(pi)
                                }
                              }}
                              role='button'
                              tabIndex={0}
                            >
                              <input
                                type='radio'
                                name='parent-radio-group'
                                checked={parent.isSelected}
                                onChange={() => handleParentToggle(pi)}
                                className='customRadioBox'
                                tabIndex={-1}
                              />
                            </div>
                          </div>

                          <div className='shopSelectorParentContent'>
                            <div className='parentRow'>
                              {parent.title && <p className='parentLabel'>{capitalizeFirstLetter(parent.title)}</p>}
                              <div className='codeLabelsContainer'>
                                {
                                  !parent.hideCode && parent.codeFields
                                    ? (
                                        parent.codeFields.map((codeField, index) => (
                                          <p key={index} className='code'>
                                            {codeField.label}: <span className='codeBox'>{codeField.code}</span>
                                          </p>
                                        ))
                                      )
                                    : !parent.hideCode && (
                                      <p className='code'>
                                        {parent.primaryCodeLabel || parent.codeLabel || 'Code'}: <span className='codeBox'>{parent.code}</span>
                                      </p>
                                      )
                                }
                              </div>
                            </div>
                            <div className='parentRow lessSpace'>
                              {nullSafeString(() => parent.address, '') && (<p className='address'>{nullSafeString(() => parent.address, '')}</p>)}
                              {hasChildren && (
                                <p className='selection'>
                                  {parent.customSelectionText
                                    ? (
                                      <>
                                        <b>{selected}</b> {parent.labelSelection || formattedMessage.selectedLabel}
                                      </>
                                      )
                                    : (
                                      <>
                                        <b>{selected}</b> {formattedMessage.ofLabel} <b>{parent.shops.length}</b>{' '}
                                        {parent.labelSelection || formattedMessage.selectedLabel}
                                      </>
                                      )}
                                </p>
                              )}
                              {parent.typologyLabel && (
                                <p className='typology'>
                                  {formattedMessage.typology}:
                                  <MerchantTableBadge symbol label={parent.typologyLabel} status='brand' />
                                </p>
                              )}
                            </div>
                          </div>

                          {hasChildren && (
                            /* eslint-disable */
                            <div
                              className={`shopSelectorIcon ${childrenVisible ? 'open' : ''}`}
                              role='button'
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  setVisibleChildren((prev) => ({ ...prev, [pi]: !prev[pi] }))
                                }
                              }}
                              aria-label={childrenVisible ? intl.formatMessage({ id: 't.app.shopSelector.collapseShops' }) : intl.formatMessage({ id: 't.app.shopSelector.expandShops' })}
                              aria-expanded={childrenVisible}
                            >
                              <PortalIconButton
                                icon={<ChevronDown size='24' type='line' />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setVisibleChildren((prev) => ({ ...prev, [pi]: !prev[pi] }))
                                }}
                              />
                            </div>
                          )}
                        </div>

                        {childrenVisible &&
                          hasChildren &&
                          parent.shops.map((child, ci) => (
                            <div
                              key={child.code}
                              className={`shopSelectorChildrenContent ${child.isSelected ? 'selected' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleChildToggle(pi, ci)
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleChildToggle(pi, ci)
                                }
                              }}
                              role='button'
                              tabIndex={0}
                            >
                              <div className='shopSelectorChildrenContainer'>
                                <div className='shopSelectorChildrenSelection'>
                                  {!child.hideCheckbox && (
                                    <div className='shopSelectorChildCheckboxWrapper'>
                                      <PortalCheckBox
                                        tabIndex={-1}
                                        ariaLabel={`Shop: ${child.labelTop || child.code}`}
                                        onChange={() => handleChildToggle(pi, ci)}
                                        options={[
                                          {
                                            label: `Shop: ${child.labelTop || child.code}`,
                                            checked: child.isSelected
                                          }
                                        ]}
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className='shopSelectorChildrenContext'>
                                  <div className='childrenHeader'>
                                    <div className='childrenRow'>
                                      {child.labelTop && <p className='childrenLabel'>{capitalizeFirstLetter(child.labelTop)}</p>}
                                    </div>
                                    <div className='childrenRow'>
                                      <p className='codeChildren'>
                                        {child.childrenCodelabel}: <span className='codeBox'>{child.code}</span>
                                      </p>
                                    </div>
                                  </div>
                                  <div className='childrenBottom'>
                                    <div className={`childrenRow ${child.address ? 'addressTypology' : ''}`}>
                                      <p className='address'>{nullSafeString(() => child.address, '')}</p>
                                      <p className='typology'>
                                        {formattedMessage.typology}:
                                        <MerchantTableBadge symbol label={child.childrenTypologyLabel} status='brand' small />
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )}
      </div>
    </Toggle>
  )
}

CostsShopSelector.propTypes = {
  data: PropTypes.shape({
    multiPiva: PropTypes.bool,
    title: PropTypes.string,
    label: PropTypes.string,
    code: PropTypes.string,
    group: PropTypes.string,
    initialParents: PropTypes.array,
    selections: PropTypes.array
  }),
  onChange: PropTypes.func.isRequired,
  shopSaved: PropTypes.array,
  tabSelected: PropTypes.string,
  clearAction: PropTypes.func,
  actions: PropTypes.object,
  shopListLoading: PropTypes.bool,
  shopSearchTerm: PropTypes.string,
  shopLocalSelection: PropTypes.object,
  noParentRowBorder: PropTypes.bool
}

CostsShopSelector.defaultProps = {
  data: {
    multiPiva: false,
    title: '',
    label: '',
    code: '',
    group: '',
    initialParents: [],
    selections: []
  },
  shopSaved: [],
  tabSelected: '',
  clearAction: () => { },
  actions: {},
  shopListLoading: false,
  shopSearchTerm: '',
  shopLocalSelection: {},
  noParentRowBorder: false
}

export default CostsShopSelector
